import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { listInvitationGroups } from "../../../api/groupsApi";
import GroupChoiceField from "../../../projects/settings/groups/GroupChoiceField";
import { groupPathName } from "../../../projects/settings/groups/groupNames";
import { useLoad } from "../../../shared/loading/useLoad";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { InviteDialog, systemGroupName } from "../../access";

/**
 * Administration › Accounts › Invite people (redesign R3-4): the group screen's dialog, choosing
 * what the invitations carry: the installation's groups the viewer may carry, as the server
 * offers them, and Administrators, which makes them administrators.
 */
export default function InvitePeopleDialog({ onInvited, onClose }: { onInvited: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [groupIds, setGroupIds] = useState<readonly string[]>([]);
  const [administrator, setAdministrator] = useState(false);
  const load = useCallback((signal: AbortSignal) => listInvitationGroups(null, signal), []);
  const { state: offer, reload } = useLoad(load);
  const offered = offer.status === "loaded" ? offer.value : [];
  const chosen = offered.filter((group) => groupIds.includes(group.groupId));
  const administrators = systemGroupName(t, "administrators");
  const joins = [...(administrator ? [administrators] : []), ...chosen.map((group) => groupPathName(group, offered))];
  return (
    <InviteDialog
      title={t("access.invite.people")}
      hint={joins.length === 0 ? t("access.invite.joinsNothing") : t("access.invite.joins", { groups: joins.join(", ") })}
      carries={{ groups: chosen.map((group) => group.groupId), accountRole: administrator ? "administrator" : "member" }}
      ready={offer.status === "loaded"}
      onInvited={onInvited}
      onClose={onClose}
      chooser={
        <>
          <LoadStatus state={offer} loadingLabel={t("common.loading")} onRetry={reload} compact={true} />
          {offer.status === "loaded" ? (
            <GroupChoiceField legend={t("projects.invitation.groups")} none={t("administration.installation.noGroups")}
              groupIds={chosen.map((group) => group.groupId)} groups={offered} onChange={setGroupIds}>
              <OptionLabel>
                <input type="checkbox" checked={administrator} onChange={(event) => { setAdministrator(event.target.checked); }} />
                {administrators}
              </OptionLabel>
            </GroupChoiceField>
          ) : null}
        </>
      }
    />
  );
}
